import { useEffect, useRef, useState } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import {
  listQuoteNoteTemplates,
  createQuoteNoteTemplate,
  updateQuoteNoteTemplate,
  deleteQuoteNoteTemplate,
  reorderQuoteNoteTemplates,
} from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";

// Quote Note Templates admin — same list UX as the claim quote lists
// (ClaimQuoteTab LineList): inline expand-to-edit, insert-after, drag-and-drop
// reorder, hard delete. Order IS the grouping: a HEADER row owns every
// non-header row after it until the next header (ticking a header on the Quote
// tab selects its whole group), so headers render bold to show the groups.

// canWrite (KR 2026-08-28): System Data is view-only below admin — the page
// passes the caller's admin state; the routes refuse non-admin writes anyway.
function QuoteNoteTemplatesManager({ canWrite = true }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [afterId, setAfterId] = useState(null); // insert-after target for "new"
  const [form, setForm] = useState({ description: "", is_header: false });
  const [error, setError] = useState("");
  const firstRef = useRef(null);

  function load() {
    return listQuoteNoteTemplates()
      .then(setItems)
      .catch(() => setError("Unable to load templates."));
  }
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (editingId) firstRef.current?.focus();
  }, [editingId]);

  function startAdd(after = null) {
    setError("");
    setForm({ description: "", is_header: false });
    setAfterId(after);
    setEditingId("new");
  }
  function startEdit(it) {
    setError("");
    setForm({ description: it.description || "", is_header: !!it.is_header });
    setEditingId(it.id);
  }
  async function save() {
    setError("");
    try {
      if (editingId === "new") {
        await createQuoteNoteTemplate({ ...form, after_id: afterId });
      } else {
        await updateQuoteNoteTemplate(editingId, form);
      }
      setEditingId(null);
      setAfterId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    }
  }
  async function remove(it) {
    if (!window.confirm("Delete this template line? Notes already saved on claims keep their text.")) return;
    setError("");
    try {
      await deleteQuoteNoteTemplate(it.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  const dragDisabled = editingId !== null || !canWrite;
  async function handleReorder(reordered) {
    const prev = items;
    setItems(reordered);
    try {
      await reorderQuoteNoteTemplates(reordered.map((l) => l.id));
    } catch (err) {
      setItems(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  function editRow(key, label) {
    return (
      <tr key={`edit-${key}`}>
        <td />
        <td className="text-muted">{label}</td>
        <td>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            ref={firstRef}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </td>
        <td className="text-center">
          <Form.Check
            type="switch"
            id={`tpl-header-${key}`}
            checked={form.is_header}
            onChange={(e) => set({ is_header: e.target.checked })}
            data-hint="Header lines are the tickable group titles on the Quote tab"
          />
        </td>
        <td className="text-end text-nowrap">
          <Button size="sm" className="me-2" onClick={save} disabled={!form.description.trim()}>
            {key === "new" ? "Add" : "Save"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => { setEditingId(null); setAfterId(null); }}>
            Cancel
          </Button>
        </td>
      </tr>
    );
  }

  function viewRow(it, idx) {
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled}>
        <td className="text-muted">{idx + 1}</td>
        <td className={it.is_header ? "fw-semibold text-uppercase" : "ps-4"} style={{ whiteSpace: "pre-wrap" }}>
          {it.description || <span className="text-muted">—</span>}
        </td>
        <td className="text-center">
          {it.is_header ? <Badge bg="secondary">Header</Badge> : <span className="text-muted">—</span>}
        </td>
        <td className="text-end text-nowrap">
          {canWrite ? (
            <>
              <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(it)}>
                Edit
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => startAdd(it.id)}
                data-hint="Insert a new line directly below this one">
                Insert
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>
                Delete
              </Button>
            </>
          ) : (
            <span className="text-muted small">—</span>
          )}
        </td>
      </SortableTr>
    );
  }

  // Rows render in list order; when inserting, the editor row appears right
  // below its insert-after target so it lands where it will live.
  const rows = [];
  for (const [idx, it] of items.entries()) {
    rows.push(editingId === it.id ? editRow(it.id, idx + 1) : viewRow(it, idx));
    if (editingId === "new" && afterId === it.id) rows.push(editRow("new", "↳"));
  }
  if (editingId === "new" && afterId === null) rows.push(editRow("new", items.length + 1));

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 className="mb-0">Quote Note Templates</h5>
          <div className="small text-muted">
            A HEADER owns every line below it until the next header — ticking a header on the Quote tab adds its whole group.
          </div>
        </div>
        {canWrite && editingId === null ? (
          <Button size="sm" variant="success" onClick={() => startAdd(null)}>
            + Add
          </Button>
        ) : null}
        {!canWrite ? <span className="small text-muted">View only — admins make changes</span> : null}
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <SortableRows items={items} onReorder={handleReorder} disabled={dragDisabled}>
        <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
          <thead className="table-surface">
            <tr>
              <th style={{ width: 36 }} />
              <th style={{ width: 44 }}>#</th>
              <th>Description</th>
              <th style={{ width: 90 }} className="text-center">Header</th>
              <th style={{ width: 220 }} className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {items.length === 0 && editingId !== "new" ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">No templates.</td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </SortableRows>
    </>
  );
}

export default QuoteNoteTemplatesManager;
